import HeaderComponent from '@/components/HeaderRefactored/HeaderRefactored';
import { usePagination } from '@/hooks/usePagination';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { sendAnalytics } from '@/services/analyticsService';
import {
	EmailSecondary,
	GoogleButtonIcon,
	MsButtonIcon
} from '@/resources/assets/images';
import { BusinessFilter } from '../components/atoms/BusinessFilter';
import { BusinessHeader } from '../components/atoms/BusinessHeader';
import { BusinessPagination } from '../components/atoms/BusinessPagination';
import { BusinessTable } from '../components/atoms/BusinessTable';
import { useListDetails } from '../hooks/useListDetails';
import { useUserData } from '../hooks/useUserData';
import { BusinessPreview } from '../components/atoms/BusinessPreview';
import { useQueryBusiness } from '../hooks/useQueryBusiness';
import { useRemoveBusinessMutation } from '../hooks/useRemoveBusinessMutation';
import { useListDetailsFiltersContext } from '../providers/ListDetailsFiltersProviders';
import { useListsDetailsParams } from '../hooks/useListsDetailsParams';

export const ListDetailsPage = () => {
	const { appliedFilters, onFiltersChange } = useListDetailsFiltersContext();

	const { listId } = useListsDetailsParams();

	const history = useHistory();

	const { list } = useListDetails({ id: listId });

	const { user } = useUserData();

	const {
		data,
		isLoading: isBusinessDataLoading,
		refetch
	} = useQueryBusiness({
		limit: 50,
		skip: 0,
		...appliedFilters,
		count: true,
		listId
	});
	const { result, count } = useMemo(
		() =>
			data?.data.data || {
				result: [],
				count: 0
			},
		[data?.data.data]
	);

	const { page, totalPages, onGotoPage, onNextPage, onPreviousPage } =
		usePagination({
			skip: appliedFilters?.skip || 0,
			count,
			limitPerPage: appliedFilters?.limit || 50,
			onPageChange: (skip: number) =>
				onFiltersChange({
					...appliedFilters,
					skip,
					listId,
					limit: 50
				})
		});

	const { mutate: mutateRemoveBusiness } = useRemoveBusinessMutation({
		currentPage: page,
		totalElements: count,
		onPreviousPage,
		listId,
		refetch
	});

	const handleRemoveBusiness = (businessId: string) => {
		mutateRemoveBusiness({ businessId, listId });
	};

	const handleReturnToListManagement = useCallback(() => {
		history.replace('/lists');
	}, [history]);

	useEffect(() => {
		if (!listId) {
			handleReturnToListManagement();
		}
	}, [handleReturnToListManagement, listId]);

	const [isReauthModalOpen, setIsReauthModalOpen] = useState(
		user?.nylasSyncStatus === 'invalid'
	);

	const handleCloseReauthenticateModal = () => {
		setIsReauthModalOpen(false);
		sendAnalytics({
			type: 'track',
			eventName: `User closed Nylas sync warning`
		});
	};

	return (
		<div className="flex h-full w-full flex-col gap-4 overflow-auto p-3">
			<HeaderComponent
				navigationText="Back to List Management"
				listName={list?.name}
				emailCounter={user.emailLimitLeft}
				totalEmails={user.emailLimit}
				navFunction={handleReturnToListManagement}
			/>

			<div className="block h-full w-full gap-4 lg:flex">
				<BusinessFilter />

				<section className="relative flex h-full w-full overflow-hidden">
					<div className="flex h-full w-full flex-col gap-4">
						<BusinessHeader
							currentPage={page}
							totalElements={count}
						/>

						<section className="flex h-full w-full flex-col gap-4 rounded-md bg-dm-black">
							<BusinessTable
								pageData={result}
								isLoading={isBusinessDataLoading}
								onRemoveBusiness={handleRemoveBusiness}
							/>

							<BusinessPagination
								currentPage={page}
								totalPages={totalPages}
								isLoading={isBusinessDataLoading}
								onNextPage={onNextPage}
								onPreviousPage={onPreviousPage}
								onGotoPage={onGotoPage}
							/>
						</section>
					</div>

					<BusinessPreview />
				</section>
			</div>

			<NylasReauthModal
				isOpen={isReauthModalOpen}
				onRemindMeLaterClick={handleCloseReauthenticateModal}
			/>
		</div>
	);
};

type NylasReauthModalProps = {
	isOpen: boolean;
	onRemindMeLaterClick: VoidFunction;
};

const NylasReauthModal = ({
	isOpen,
	onRemindMeLaterClick
}: NylasReauthModalProps) => {
	if (!isOpen) return null;

	return (
		<div className="email-failure-redirect">
			<div className="email-failure-reauthenticate">
				<div className="reauthenticate-holder">
					<div className="reauthenticate-header">
						We need to re-authenticate your Email to access
						communication features
					</div>

					<div className="reauthenticate-text">
						This can be caused by email account password or other
						company email settings being changed. If you recently
						verified your email, please wait 10 minutes before
						re-verifying.
					</div>

					<Link
						to="/verify-email-onboarding"
						className="signup-button flex items-center justify-center space-x-2"
					>
						<GoogleButtonIcon className="email-logo email-logo-a" />
						<MsButtonIcon className="email-logo email-logo-b" />
						<EmailSecondary className="email-logo email-logo-c" />
						<span className="button-text-redesign">
							Verify Your Email
						</span>
					</Link>

					<button
						type="button"
						onClick={onRemindMeLaterClick}
						className="reauthenticate-remind-me-later"
					>
						Remind me later
					</button>
				</div>
			</div>
		</div>
	);
};
